import { Info, Warning } from 'shared/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../services/api';
import ErrorCard from '../../ui/ErrorCard';
import { SkeletonTable } from '../../ui/Skeleton';
import StandardTable from '../../ui/StandardTable';
import LocationInfoPanel from '../LocationInfoPanel';
import { HEAT_BLOCK_STYLES } from '../map/WarehouseMap';
import WarehouseMap from '../map/WarehouseMap';
import { NormalizeLayout } from '../map/mapModel';
import { GetOccupancy } from './inventoryApi';

/**
 * OCUPAÇÃO — heatmap no mapa + espaço ocioso + custo (M9, itens 1 e 2).
 *
 * ── A REGRA QUE ESTA TELA PRECISA CONTAR ────────────────────────────────────
 * O andar 5 existe, está bloqueado porque a empilhadeira não alcança, e por
 * isso ele fica **fora do denominador** de capacidade e **dentro** do espaço
 * ocioso (§5). São dois números que parecem contraditórios na mesma tela — e é
 * justamente por isso que a tela DIZ a regra, em vez de deixar quem lê deduzir
 * por que 432 posições viraram 360 de capacidade.
 *
 * ⚠️ O mapa é o `WarehouseMap` de sempre, em modo `heatmap`. Não existe um
 * segundo mapa: o modo entrou registrando um renderer, como o M5 fez com a rota.
 */

const FIT_HEIGHT = 460;

/** Faixas na ordem do termômetro — a legenda usa a MESMA tabela do canvas. */
const LEGEND_BANDS = ['EMPTY', 'LOW', 'MEDIUM', 'HIGH', 'FULL', 'UNAVAILABLE'];

const FormatInt = (value) => new Intl.NumberFormat('pt-BR').format(Number(value ?? 0));
const FormatPct = (value) => (value === null || value === undefined ? '—' : `${Number(value).toFixed(1)}%`);
const FormatBrl = (value) =>
    value === null || value === undefined
        ? '—'
        : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value));

const StatCard = ({ title, value, hint, tone = 'default' }) => (
    <div
        className={`rounded-xl border p-4 ${
            tone === 'warn'
                ? 'border-warning-300 bg-warning-soft dark:border-warning-800 dark:bg-warning-900/20'
                : 'border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-900'
        }`}
    >
        <p className="text-xs font-medium uppercase tracking-wide text-mono-500 dark:text-mono-400">{title}</p>
        <p className="mt-1 text-2xl font-bold text-mono-900 dark:text-mono-50">{value}</p>
        {hint ? <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">{hint}</p> : null}
    </div>
);

const InventoryOccupancyPanel = ({ label, selectedBlocks = null, onBlockToggle = null }) => {
    const [data, setData] = useState(null);
    const [layout, setLayout] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    // Modo DIRETORIA (sem onBlockToggle): clicar no mapa abre o drawer com o
    // conteúdo da posição/bloco (o "ver o que tem ali"). No modo de SELEÇÃO de
    // blocos (contagem), o clique continua marcando/desmarcando.
    const isDirectorView = !onBlockToggle;
    const [panelTarget, setPanelTarget] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const [occupancy, mapLayout] = await Promise.all([GetOccupancy(), api.getWarehouseMapLayout()]);
            setData(occupancy);
            setLayout(NormalizeLayout(mapLayout?.layout));
        } catch (error) {
            setLoadError(error?.message || 'Erro ao carregar');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const handleSelect = useCallback(
        (selection) => {
            if (onBlockToggle) {
                // Modo seleção (contagem): só blocos, marca/desmarca. `rack` é a
                // LETRA do porta-pallet, `bay` o nº do bloco.
                if (selection?.kind === 'block') {
                    onBlockToggle({ rack: selection.rack, bay: selection.bay });
                }
                return;
            }
            // Modo diretoria: qualquer alvo abre o drawer (bloco/rack/posição).
            setPanelTarget(selection ?? null);
        },
        [onBlockToggle],
    );

    const levelColumns = useMemo(
        () => [
            {
                key: 'level',
                label: label('wms.field.level'),
                getValue: (row) => row.level ?? 0,
                render: (row) => <span className="font-mono">A{row.level}</span>,
            },
            {
                key: 'total_positions',
                label: label('wms.occupancy.total_positions'),
                getValue: (row) => row.total_positions,
                render: (row) => FormatInt(row.total_positions),
            },
            {
                key: 'capacity_positions',
                label: label('wms.occupancy.capacity_positions'),
                getValue: (row) => row.capacity_positions,
                render: (row) => FormatInt(row.capacity_positions),
            },
            {
                key: 'occupied_positions',
                label: label('wms.occupancy.occupied_positions'),
                getValue: (row) => row.occupied_positions,
                render: (row) => FormatInt(row.occupied_positions),
            },
            {
                key: 'blocked_positions',
                label: label('wms.occupancy.blocked_positions'),
                getValue: (row) => row.blocked_positions,
                render: (row) =>
                    row.blocked_positions > 0 ? (
                        <span className="font-semibold text-warning-text dark:text-warning-300">
                            {FormatInt(row.blocked_positions)}
                        </span>
                    ) : (
                        FormatInt(0)
                    ),
            },
            {
                key: 'occupancy_pct',
                label: label('wms.occupancy.occupancy_pct'),
                getValue: (row) => row.occupancy_pct ?? -1,
                render: (row) => FormatPct(row.occupancy_pct),
            },
        ],
        [label],
    );

    if (loading && !data) {
        return <SkeletonTable columns={6} rows={5} />;
    }

    if (loadError) {
        return (
            <ErrorCard
                title={label('wms.heatmap.title')}
                message={loadError}
                action={label('wms.common.retry')}
                actionFn={load}
            />
        );
    }

    const summary = data?.summary ?? {};
    const cost = data?.cost ?? {};

    return (
        <div className="space-y-6">
            {/* A REGRA DO §5, escrita onde os dois números convivem. */}
            <div className="flex items-start gap-3 rounded-xl border-2 border-info-400 bg-info-soft px-4 py-3 dark:border-info-700 dark:bg-info-900/30">
                <Info size={22} className="mt-0.5 shrink-0 text-info-text dark:text-info-300" />
                <p className="text-sm text-info-text dark:text-info-200">{label('wms.heatmap.blocked_rule')}</p>
            </div>

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <StatCard
                    title={label('wms.occupancy.capacity_positions')}
                    value={FormatInt(summary.capacity_positions)}
                    hint={`${FormatInt(summary.total_positions)} ${label('wms.occupancy.total_positions').toLowerCase()}`}
                />
                <StatCard
                    title={label('wms.occupancy.occupancy_pct')}
                    value={FormatPct(summary.occupancy_pct)}
                    hint={`${FormatInt(summary.occupied_positions)} / ${FormatInt(summary.capacity_positions)}`}
                />
                <StatCard
                    title={label('wms.occupancy.idle_positions')}
                    value={FormatInt(summary.idle_positions)}
                    hint={`${FormatInt(summary.idle_available_positions)} ${label('wms.occupancy.idle_available').toLowerCase()}`}
                />
                <StatCard
                    tone="warn"
                    title={label('wms.occupancy.idle_blocked')}
                    value={FormatInt(summary.idle_blocked_positions)}
                    hint={FormatPct(summary.idle_blocked_pct)}
                />
            </div>

            {/* CUSTO: com parâmetro, o valor; sem parâmetro, o que falta. Nunca
                um número redondo inventado. */}
            {cost.configured ? (
                <section className="space-y-3">
                    <h3 className="text-base font-semibold text-mono-900 dark:text-mono-50">
                        {label('wms.occupancy.cost_title')}
                    </h3>
                    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
                        {/* CUA/TPP — o "aluguel" de uma posição, o número-base. */}
                        <StatCard
                            title={label('wms.occupancy.cua')}
                            value={`${FormatBrl(cost.cost_per_position_day)}/dia`}
                            hint={`${FormatBrl(cost.cost_per_position)}/mês · ÷${cost.days_in_month}`}
                        />
                        {/* O espaço que TRABALHA. */}
                        <StatCard
                            title={label('wms.occupancy.cost_occupied')}
                            value={`${FormatBrl(cost.occupied_daily_cost)}/dia`}
                            hint={`${FormatBrl(cost.occupied_monthly_cost)}/mês`}
                        />
                        {/* O vazio que dá pra usar hoje. */}
                        <StatCard
                            title={label('wms.occupancy.cost_idle')}
                            value={`${FormatBrl(cost.idle_daily_cost)}/dia`}
                            hint={`${FormatBrl(cost.idle_monthly_cost)}/mês`}
                        />
                        {/* Andar/posição bloqueada — só se usa comprando máquina. */}
                        <StatCard
                            tone="warn"
                            title={label('wms.occupancy.cost_blocked')}
                            value={`${FormatBrl(cost.blocked_daily_cost)}/dia`}
                            hint={`${FormatBrl(cost.blocked_monthly_cost)}/mês`}
                        />
                        {/* COR — o "dinheiro parado" em % do custo total. */}
                        <StatCard
                            tone="warn"
                            title={label('wms.occupancy.cor')}
                            value={FormatPct(cost.cor_pct)}
                            hint={label('wms.occupancy.cor_hint')}
                        />
                    </div>
                </section>
            ) : (
                <div className="flex items-start gap-3 rounded-xl border border-mono-200 bg-mono-50 px-4 py-3 dark:border-mono-800 dark:bg-mono-900">
                    <Warning size={20} className="mt-0.5 shrink-0 text-mono-500" />
                    <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.occupancy.cost_missing')}</p>
                </div>
            )}

            <section className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-base font-semibold text-mono-900 dark:text-mono-50">
                        {label('wms.heatmap.title')}
                    </h3>
                    <div className="flex flex-wrap items-center gap-3">
                        {LEGEND_BANDS.map((band) => (
                            <span key={band} className="flex items-center gap-1.5 text-xs text-mono-600 dark:text-mono-400">
                                <span
                                    className="inline-block h-3 w-4 rounded-sm border"
                                    style={{
                                        backgroundColor: HEAT_BLOCK_STYLES[band].fill,
                                        borderColor: HEAT_BLOCK_STYLES[band].stroke,
                                    }}
                                />
                                {label(`wms.heatmap.band.${band}`)}
                            </span>
                        ))}
                    </div>
                </div>
                {layout ? (
                    <div className="flex flex-col gap-4 xl:flex-row xl:items-stretch">
                        <div className="min-w-0 flex-1">
                            <WarehouseMap
                                mode="heatmap"
                                layout={layout}
                                heatmap={data?.blocks ?? []}
                                selectedBlocks={selectedBlocks}
                                onSelect={handleSelect}
                                fitHeight={FIT_HEIGHT}
                            />
                        </div>
                        {/* Painel lateral (visão diretoria): clicou no mapa,
                            vê o que tem ali — a torre do bloco + as posições. */}
                        {isDirectorView ? (
                            <aside className="w-full shrink-0 xl:w-[360px]">
                                <div className="h-full xl:sticky xl:top-4">
                                    <LocationInfoPanel
                                        target={panelTarget}
                                        label={label}
                                        onClear={() => setPanelTarget(null)}
                                        onSelectLocation={(row) =>
                                            setPanelTarget({ kind: 'location', location: row })
                                        }
                                    />
                                </div>
                            </aside>
                        ) : null}
                    </div>
                ) : (
                    <p className="rounded-xl border border-dashed border-mono-300 p-6 text-center text-sm text-mono-500 dark:border-mono-700">
                        {label('wms.map.empty')}
                    </p>
                )}
                <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.heatmap.blocked_hatch')}</p>
            </section>

            <section className="space-y-3">
                <h3 className="text-base font-semibold text-mono-900 dark:text-mono-50">
                    {label('wms.occupancy.by_level')}
                </h3>
                <StandardTable
                    rows={data?.levels ?? []}
                    columns={levelColumns}
                    rowKey={(row) => `level-${row.level}`}
                    countLabel={label('wms.occupancy.by_level')}
                    exportFileName="wms-ocupacao-por-andar"
                    emptyMessage={label('wms.map.no_block_data')}
                    searchEnabled={false}
                />
            </section>
        </div>
    );
};

export default InventoryOccupancyPanel;
